import { Box, Button, Checkbox, Flex, Grid, Heading, Highlight, Image, Modal, ModalBody, ModalContent, ModalFooter, ModalOverlay, Text, useDisclosure } from '@chakra-ui/react';
import { useFormik } from 'formik';
import HtmlToReact from 'html-to-react';
import { useEffect, useMemo, useState } from 'react';
import { BsCart4 } from 'react-icons/bs';
import { HiXCircle } from 'react-icons/hi2';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavigationScroll from '../../../components/Navigation/NavigationScroll';
import { trackAddToCart } from '../../../helper/utils/eventTrackGA';
import { formattedDate3 } from '../../../helper/utils/formattedDate';
import { isEmptyObject } from '../../../helper/utils/isEmptyObject';
import { urlNavigate } from '../../../helper/utils/navigation';
import { safeJsonParse } from '../../../helper/utils/safeJsonParse';
import { doHandleState } from '../../../redux/onlineSales/onlineSales.actions';
import { doError } from '../../../redux/responseHandling/responseHandling.actions';
import { isCartContainRecurring, isItemContainRecurring } from '../../../redux/utils/menuItem';
import useOnlineSales from '../hooks/useOnlineSales';
import usePages from '../hooks/usePages';
import ModalConfirm from './ModalConfirm';
import ProductDates from './ProductDates';
import ProductDesc from './ProductDesc';
// import ProductRooms from './ProductRooms';
import { faqPartyPPI } from '../../../mock/faq_party_ppi';
import { faqPartySPI } from '../../../mock/faq_party_spi';
import { faqPartyTSI } from '../../../mock/faq_party_tsi';
import useCartDatas from '../hooks/useCartDatas';
import CustomerInformation from '../pages/checkout/widget/CustomerInformation';
import ProductTickets from './ProductTickets';
import ProductTimeSlot from './ProductTimeSlot';

const HtmlToReactParser = HtmlToReact.Parser;
export default function ProductDetailOverview({ productType = 'ticketing', pageName = '', productId = '' }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const product = safeJsonParse(localStorage.getItem('product'), {});

    const { isOpen: isOpenConfirm, onOpen: onOpenConfirm, onClose: onCloseConfirm } = useDisclosure();
    const { isOpen: isOpenTnc, onOpen: onOpenTnc, onClose: onCloseTnc } = useDisclosure();
    const { cart, handleEditCart, handleAddToCart, loadingDetail, loadingOrder, detail, selectedDate, isCartContainTimeSlot, isCartContainDate, handleSelectDetailDate, selectedDetailDate, handleSelectDetailTimeslot, selectedDetailTimeslot, getMinQty, getMaxQty, handleGetProductAvailability, availabilityStatusActive, selectedDetailCurrentDate, handleSelectDetailCurrentDate, handleToggleDetailRoom, selectedDetailRooms, selectedAddonsDetail, handleSelectAddonDetail, page, roomPartyAvailability } =
        useOnlineSales();

    const { detail: data = {}, availability = [], sessions = [], combo = [], rooms = [], packages = [] } = detail || {};
    const { productId: idProduct } = usePages();
    const { isVerifiedWaiver = false, minor = [] } = useSelector((state) => state?.waiver);

    const [productData, setProductData] = useState({});
    const [searchParams, _] = useSearchParams();

    const initialValues = useMemo(
        () => ({
            childrens: [],
            qty: getMinQty(data),
            timeslot: {},
        }),
        [],
    );

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
    });

    const pageId = useMemo(() => (page?.['id'] || '').toString().trim().toLowerCase(), [page]);
    const menuType = useMemo(() => data['menuType'] || '', [data]);
    const isMenuParty = useMemo(() => menuType === 'party', [menuType]);
    const isMenuMembership = useMemo(() => menuType === 'membership', [menuType]);
    const isHaveTimeslot = isCartContainTimeSlot();
    const isHaveCurrentDate = isCartContainDate();
    const existMenu = cart.find((item) => item['menuCode'] === data['menuCode'] && !item['isMenuAddOns'] && (item['rooms'] || []).length === 0) || {};
    const isExistMenu = !isEmptyObject(existMenu);
    const isNeedTimeslot = isEmptyObject(data) ? false : +(data['isNeedTimeSlot'] || '0') === 1;

    const isItemSubscription = isItemContainRecurring(combo);
    const isItemMembership = useMemo(() => isMenuMembership, [product, isMenuMembership]);
    const isItemRecurring = isItemSubscription || isItemMembership;
    const isCartRecurring = isCartContainRecurring(cart);

    const checkedItems = formik.values.childrens;
    const filteredMinors = useMemo(() => {
        const waiverRank = (value) => {
            if (value === true) return 1;
            if (value === false || value === null || value === undefined) return 0;

            const numericValue = Number(value);
            if (!Number.isNaN(numericValue)) return numericValue;

            const str = String(value).trim().toLowerCase();
            if (str === 'y' || str === 'yes' || str === 'true') return 1;
            if (str === 'n' || str === 'no' || str === 'false') return 0;

            return 0;
        };

        const filtered = (minor || []).filter((item) => {
            if (isItemMembership) {
                return +(item['statusCode'] || '0') === 1 && item['memberNo'] !== '';
            }

            return true;
        });

        return [...filtered].sort((a, b) => waiverRank(b?.['isWaiver']) - waiverRank(a?.['isWaiver']));
    }, [minor, detail]);

    const faqParty = useMemo(() => {
        if (pageId === 'spi') {
            return faqPartySPI;
        }
        if (pageId === 'ppi') {
            return faqPartyPPI;
        }
        if (pageId === 'tsi') {
            return faqPartyTSI;
        }

        return '';
    }, [pageId]);

    const isChecked = (id) => checkedItems.includes(id);

    const handleClose = () => {
        formik.handleReset();

        onCloseConfirm();
        setProductData({});

        dispatch(doHandleState('detail', {}));

        if (selectedDetailDate) {
            handleGetProductAvailability(productType, formattedDate3(selectedDetailDate));
        }

        navigate(`${searchParams.get('callback') || urlNavigate('online-sales', 'home')}?isScrollToProduct=true`);
    };

    const toggleItem = (id) => {
        if (checkedItems.includes(id)) {
            const filteredMinors = checkedItems.filter((itemId) => itemId !== id);
            formik.setFieldValue('childrens', filteredMinors);

            setProductData((prev) => ({
                ...prev,
                qty: filteredMinors.length,
            }));
        } else {
            const filteredMinors = [...checkedItems, id];
            formik.setFieldValue('childrens', filteredMinors);

            setProductData((prev) => ({
                ...prev,
                qty: filteredMinors.length,
            }));
        }
    };

    const { handleShowCapacity } = useCartDatas();

    const buildPartyCapacityPayload = (time) => ({
        outletCode: page?.outletCode ?? '',
        startTime: time?.StartTime ?? '00:00:00',
        refDate: formattedDate3(selectedDate || new Date()),
        brandCode: page?.brandCode ?? '',
    });

    const handleSelectTimeSlot = (time) => {
        formik.setFieldValue('childrens', []);
        if (isHaveTimeslot) return;

        handleSelectDetailTimeslot(time);

        if (productType === 'party') {
            handleShowCapacity(buildPartyCapacityPayload(time));
        }

        setProductData((prev) => ({
            ...prev,
            timeSlot: time,
            qty: getMinQty(data),
        }));
    };

    const tncSubscription = `
        1. Subscription Benefits:

            a) Unlimited Access: Enjoy 2 Hours playtime visit for (1 Child + 1 Adult) to Tayo Station Bintaro everyday including Weekends and Public Holidays
            b) Exclusive Discounts: Receive a 15% discount on DIY Activity and Special/Class Event.
            c) Partner Discounts: Eligible to receive discounts to any active retail partner of Tayo Station Bintaro

        2. Early Termination and Penalties:

            a) Minimum Commitment Period: A minimum commitment of 3 months is required.
            b) Early Termination Fee: Canceling the subscription before completing the 3-month term incurs a fee of IDR 249,000 for each remaining month of the term.

        3. Subscription Terms:

            a)  Activation Fee: Activation Fee of IDR 150,000 to join subscription include Subscription Card and Welcome Pack.
            b) Indefinite Recurring Payment: Membership is billed monthly at IDR 249,000. After completing the 3-month commitment, members may cancel at any time or let the membership continue indefinitely.
            c) Reinstatement Fee: Reinstatement fee of IDR 150,000 will be payable if a member cancels and later seeks to reinstate or reapply for membership.

        4. Payment Terms:

            a) Payment Method: Credit card payment only.
            b) Payment Default: In the event of a failed deduction, subscription code will be deactivated automatically.

        5. Membership Welcome Pack:

            New members will receive a welcome pack, which could include a lanyard, merchandise, or socks. Items in the welcome pack are subject to availability, and Tayo Station Bintrao reserves the right to modify or discontinue this offer at any time.
    `;

    const canAddToCart = useMemo(() => {
        if (!isVerifiedWaiver && isItemMembership) return false;
        if (!availabilityStatusActive) return false;
        if (isItemSubscription && isCartRecurring) return false;
        console.log('SAMPE SINI GAK?', productData);
        if ((productData['qty'] || 0) < 1) return false;
        if (isItemMembership && (filteredMinors.length === 0 || checkedItems.length === 0)) return false;

        if (isNeedTimeslot) {
            const hasSessions = (sessions || []).length > 0;
            const hasSelectedTimeslot = selectedDetailTimeslot && !isEmptyObject(selectedDetailTimeslot);
            return hasSessions && hasSelectedTimeslot;
        }

        return true;
    }, [availabilityStatusActive, isItemSubscription, isCartRecurring, productData, isNeedTimeslot, sessions]);

    useEffect(() => {
        if (!isEmptyObject(data) && (sessions || []).length > 0 && isNeedTimeslot) {
            const timeslot = sessions[0] || {};

            if (!isEmptyObject(timeslot)) {
                handleSelectTimeSlot(timeslot);
            }
        }
    }, [sessions, data, isHaveTimeslot, isNeedTimeslot]);

    useEffect(() => {
        if (isItemRecurring) {
            setProductData((prev) => ({
                ...prev,
                date: new Date(),
                timeSlot: selectedDetailTimeslot,
                qty: getMinQty(data),
            }));
        }

        if ((isHaveTimeslot || isHaveCurrentDate) && !isItemSubscription) {
            setProductData((prev) => ({
                ...prev,
                date: isHaveCurrentDate ? selectedDetailCurrentDate : selectedDetailDate || selectedDate || new Date(),
                timeSlot: availability.length > 0 ? selectedDetailTimeslot : {},
                qty: isItemMembership ? 0 : 1,
            }));
        }

        formik.handleReset();
    }, []);

    return (
        <NavigationScroll>
            <Box w='full'>
                <Box w='full' mb={14}>
                    <Flex
                        w='full'
                        alignItems='start'
                        justifyContent='space-between'
                        flexDirection={{
                            base: 'column-reverse',
                            md: 'row',
                        }}>
                        <Box>
                            <Heading
                                fontWeight={600}
                                fontSize={{
                                    base: '2xl',
                                    md: '3xl',
                                }}
                                mb={3}>
                                {data['onlineName'] ?? data['menuName'] ?? ''}
                            </Heading>
                            <ProductDesc desc={data['menuDescription']} startHeight={(data['menuDescription'] || '') !== '' ? 20 : 0} />
                            {isMenuParty && (
                                <Box w='full' my={5} display='flex' flexDirection='column' gap={2} alignItems='start'>
                                    <Button variant='link' onClick={onOpenTnc}>
                                        <Highlight query={['FAQ']} styles={{ fontWeight: 600, textDecoration: 'underline', color: 'primary.600', mx: 1 }}>
                                            Click here for Birthday Party FAQ
                                        </Highlight>
                                    </Button>
                                </Box>
                            )}
                        </Box>
                        <Box
                            w={{ base: 'full', md: '35%' }}
                            textAlign={{
                                base: 'center',
                                md: 'right',
                            }}>
                            <Image mx='auto' src={data['imageUrl']} fit='contain' />
                        </Box>
                    </Flex>
                </Box>

                {isItemSubscription && (
                    <Box w='full' my={7} bg='gray.100' border='1px' borderColor='primary.400' p={3} rounded='lg'>
                        <Heading fontSize={{ base: 'xl', md: '2xl' }} maxW={{ base: 'full', md: '75%' }} mb={1}>
                            Tayo Station Bintaro Subscription T&C Acknowledgement
                        </Heading>
                        <Text fontSize='sm' whiteSpace='pre-line'>
                            {tncSubscription}
                        </Text>
                        <Text fontSize='sm' fontWeight={600} mt={3}>
                            Waiver and Consent: By joining the Subscription Program, I acknowledge and accept the waiver and consent on behalf of myself and any accompanying minors.
                        </Text>
                    </Box>
                )}

                {availability.length > 0 && !isItemSubscription && (
                    <Box w='full' mt={isHaveTimeslot || isItemSubscription ? 0 : 14}>
                        <ProductDates
                            productId={idProduct || productId}
                            defaultDate={selectedDetailDate || selectedDate || new Date()}
                            isDisabled={isHaveTimeslot || isItemSubscription}
                            isLoading={loadingDetail}
                            availability={availability}
                            initialStartDate={selectedDetailDate || selectedDate || new Date()}
                            onSelectDate={(date) => {
                                handleSelectDetailDate(date);
                                handleSelectDetailCurrentDate(date);
                                handleSelectDetailTimeslot({});

                                setProductData({
                                    ...productData,
                                    date,
                                    timeSlot: {},
                                    qty: getMinQty(data),
                                });
                            }}
                        />
                    </Box>
                )}
                {isNeedTimeslot && isMenuParty && (
                    <Box w='full' mt={isHaveTimeslot ? 0 : 8}>
                        <ProductTimeSlot
                            isDisabled={isHaveTimeslot || !availabilityStatusActive}
                            isLoading={loadingDetail}
                            product={{
                                ...productData,
                                date: selectedDate,
                            }}
                            timeslots={sessions}
                            onSelectTime={handleSelectTimeSlot}
                        />
                    </Box>
                )}
                {/* {isMenuParty && (
                    <Box w='full' mt={8}>
                        <ProductRooms
                            isLoading={loadingDetail}
                            rooms={rooms}
                            onSelectRoom={(room) => {
                                formik.setFieldValue('childrens', []);
                                handleToggleDetailRoom(room);
                            }}
                        />
                    </Box>
                )} */}
                <Box w='full' mt={8}>
                    <Heading fontSize={{ base: 'xl', md: 'xl' }} mb={5} fontWeight={500}>
                        Select {productType === 'party' ? 'Party' : 'Ticket'}
                    </Heading>

                    <ProductTickets
                        isLoading={loadingDetail}
                        tickets={[data]}
                        combo={combo}
                        isProductRecurring={isItemRecurring || !availabilityStatusActive}
                        selectedData={productData}
                        selectedItems={[]}
                        minQty={productType === 'party' ? 0 : getMinQty(data)}
                        maxQty={productType === 'party' ? roomPartyAvailability?.roomsRemaining || 0 : getMaxQty(data)}
                        onChangeQty={(item, qty) => {
                            setProductData({
                                ...productData,
                                qty,
                            });
                        }}
                    />
                </Box>

                {productType === 'party' && productData?.qty > 0 && <MenuPackages packages={packages} productData={productData} selectedAddonsDetail={selectedAddonsDetail} loadingDetail={loadingDetail} handleSelectAddonDetail={handleSelectAddonDetail} />}

                {(isItemSubscription ? !isCartRecurring : isItemMembership) && (
                    <Box w='full' mt={7}>
                        {(filteredMinors || []).length > 0 ? (
                            <Box>
                                <Flex w='full' alignItems='center' justifyContent='space-between'>
                                    <Heading fontSize={{ base: 'xl', md: 'xl' }} mb={5} fontWeight={500}>
                                        Select For?
                                    </Heading>
                                    <Text fontSize='sm' color='red'>
                                        *Required
                                    </Text>
                                </Flex>
                                <Box w='full'>
                                    <Box w='full'>
                                        <Grid w='full' gridTemplateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }} gap={2} mt={3} spacing={3}>
                                            {(filteredMinors || [])?.map((data, index) => {
                                                const id = data['childrenID'] || '';
                                                const createdAt = data['expDate'] || data['created_at'] || '';

                                                let expirationDate = null;
                                                let daysUntilExpiration = 0;
                                                let isActive = !isItemMembership;
                                                let expirationText = '';
                                                const isWaiver = +(data['isWaiver'] || '0') === 1;

                                                if (createdAt && isItemMembership) {
                                                    const created = new Date(createdAt);
                                                    expirationDate = new Date(created);

                                                    const today = new Date();
                                                    const diffTime = expirationDate - today;
                                                    daysUntilExpiration = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                                                    isActive = daysUntilExpiration > 0;

                                                    if (isActive) {
                                                        expirationText = `${daysUntilExpiration} Day Left'${daysUntilExpiration !== 1 ? 's' : ''}`;
                                                    } else {
                                                        expirationText = `Expired ${Math.abs(daysUntilExpiration)} day${Math.abs(daysUntilExpiration) !== 1 ? 's' : ''} ago`;
                                                    }
                                                }

                                                return (
                                                    <Box
                                                        key={id || index}
                                                        p={4}
                                                        borderWidth='2px'
                                                        borderColor={isChecked(id) ? 'primary.500' : 'gray.200'}
                                                        borderRadius='lg'
                                                        cursor={isActive ? 'pointer' : 'not-allowed'}
                                                        opacity={isActive ? 1 : 0.2}
                                                        position='relative'
                                                        overflow='hidden'
                                                        _disabled={{
                                                            opacity: 0.5,
                                                            cursor: 'not-allowed',
                                                        }}
                                                        onClick={() => {
                                                            if (isActive) {
                                                                toggleItem(id);
                                                            }
                                                        }}
                                                        transition='all 0.2s'
                                                        _hover={
                                                            isActive && {
                                                                borderColor: 'primary.300',
                                                                shadow: 'md',
                                                            }
                                                        }
                                                        bg={isChecked(id) ? 'primary.50' : 'white'}>
                                                        <Flex alignItems='center' justifyContent='space-between'>
                                                            <Box flex='1'>
                                                                <Flex alignItems='center' gap={2} mb={1}>
                                                                    <Text fontSize='md' fontWeight='600' color='gray.800'>
                                                                        {data['name'] || ''}
                                                                    </Text>
                                                                    {isItemMembership && (
                                                                        <Box px={2} py={0.5} borderRadius='md' bg={isActive ? 'green.100' : 'red.100'} fontSize='xs' fontWeight='600' color={isActive ? 'green.700' : 'red.700'}>
                                                                            {isActive ? 'ACTIVE' : 'INACTIVE'}
                                                                        </Box>
                                                                    )}
                                                                    {isWaiver && (
                                                                        <Box px={2} py={0.5} borderRadius='md' bg={'orange.100'} fontSize='xs' fontWeight='600' color={'orange.700'}>
                                                                            Waiver
                                                                        </Box>
                                                                    )}
                                                                </Flex>
                                                                {createdAt && isItemMembership && (
                                                                    <Text fontSize='sm' color={isActive ? 'gray.600' : 'red.500'}>
                                                                        Expired at {formattedDate3(expirationDate)} ( {expirationText})
                                                                    </Text>
                                                                )}
                                                            </Box>
                                                            <Checkbox
                                                                isDisabled={!isActive}
                                                                size='lg'
                                                                isChecked={isChecked(id)}
                                                                onChange={() => {
                                                                    if (isActive) {
                                                                        toggleItem(id);
                                                                    }
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                }}
                                                                colorScheme='primary'
                                                            />
                                                        </Flex>
                                                    </Box>
                                                );
                                            })}
                                        </Grid>
                                    </Box>
                                </Box>
                            </Box>
                        ) : isVerifiedWaiver ? (
                            <Box w='full'>
                                <Heading fontSize={{ base: 'xl', md: '2xl' }} mb={1}>
                                    Not yet a Member?
                                </Heading>
                                <Text fontSize={{ base: 'md', md: 'lg' }}>Register as Member to checkout this item</Text>
                                <Button colorScheme='primary' rounded='lg' size='lg' fontSize='md' mt={7} onClick={() => navigate(urlNavigate('dashboard', 'membership'))}>
                                    Buy Membership
                                </Button>
                            </Box>
                        ) : (
                            <CustomerInformation />
                        )}
                    </Box>
                )}

                {!isEmptyObject(detail) && (
                    <Box w='full' mt={14} display='flex' alignItems='center' justifyContent='end' gap={3}>
                        <Flex w='full' alignItems='center' justifyContent='end' gap={3} flexWrap='wrap'>
                            {!availabilityStatusActive && (
                                <Text fontSize={14} color='red.500' fontWeight={600}>
                                    Product Not Available. Please select another date.
                                </Text>
                            )}
                            {isNeedTimeslot && sessions.length == 0 && (
                                <Text fontSize={14} color='red.500' fontWeight={600}>
                                    Product Not Available. There is no session available for this date.
                                </Text>
                            )}
                            {isItemSubscription && isCartRecurring && (
                                <Text color='red.500' fontWeight={600} fontStyle='italic'>
                                    *You cannot purchase more than 1 {isItemMembership ? 'Membership' : 'Subscription'} item.
                                </Text>
                            )}
                        </Flex>
                        <Button onClick={handleClose} variant='ghost' colorScheme='red' px={7} py={7} fontSize='14px' leftIcon={<HiXCircle />}>
                            Close
                        </Button>
                        {canAddToCart && (
                            <Button w='full' isLoading={loadingDetail || loadingOrder} px={7} py={7} fontSize='14px' colorScheme='primary' variant='solid' leftIcon={<BsCart4 fontSize='18px' />} onClick={onOpenConfirm}>
                                {isMenuParty ? 'Continue' : `Add Cart ( ${productData['qty']} )`}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            <ModalConfirm
                isOpen={isOpenConfirm}
                onClose={onCloseConfirm}
                onConfirm={() => {
                    onCloseConfirm();

                    if (!isEmptyObject(data)) {
                        const productTimeslot = isHaveTimeslot ? selectedDetailTimeslot : productData['timeSlot'] || {};
                        const mappedMinors = minor.filter((item) => checkedItems.includes(item['childrenID']));

                        if (isNeedTimeslot) {
                            if (isEmptyObject(productTimeslot)) {
                                dispatch(doError('Please select timeslot first, and make sure the timeslot is available', 'TIMESLOT REQUIRED !', '! Add to Cart'));

                                return;
                            }
                        }

                        if (isItemMembership) {
                            if (checkedItems.length === 0 || formik.values.childrens.length === 0) {
                                dispatch(doError('Please fill in the member data first.', 'MEMBER DATA REQUIRED !', '! Add to Cart'));

                                return;
                            }
                        }

                        // if (isMenuParty) {
                        //     dispatch(doError('Please select room first', 'ROOM REQUIRED !', '! Add to Cart'));

                        //     return;
                        // }

                        const payload = {
                            ...productData,
                            date: productData['date'] || selectedDate || new Date(),
                            timeSlot: isNeedTimeslot ? productTimeslot : {},
                            minors: isItemRecurring ? mappedMinors : [],
                            rooms: selectedDetailRooms || [],
                            packages: selectedAddonsDetail || [],
                            product: {
                                ...data,
                                combo,
                            },
                        };

                        if (isExistMenu) {
                            const existQty = parseFloat(existMenu['qty'] || '1');
                            const productQty = parseFloat(productData['qty'] || '1');
                            const product = productData['product'] || {};
                            const timeSlot = existMenu['timeSlot'] || {};

                            const maxCapacity = getMaxQty(product);
                            const qty = existQty + productQty;
                            const editPayload = { qty: qty >= maxCapacity ? maxCapacity : qty, timeSlot: isNeedTimeslot ? timeSlot : {}, date: existMenu['date'], id: existMenu['id'] };

                            const editProduct = payload['product'] || {};
                            const price = parseFloat(editProduct['basePrice'] || 0);
                            const value = price * qty;

                            const trackPayload = {
                                value: value,
                                currency: 'IDR',
                                items: [
                                    {
                                        item_id: editProduct['menuCode'] || '',
                                        item_name: editProduct['menuName'] || '',
                                        item_category: product['cateCode'] || '',
                                        price: +(editProduct['basePrice'] || 0),
                                        quantity: qty,
                                    },
                                ],
                            };

                            trackAddToCart(trackPayload);
                            handleEditCart(editPayload, () => {
                                handleClose();
                            });
                            return;
                        }

                        const qty = +(payload['qty'] || 1);
                        const product = payload['product'] || {};
                        const price = parseFloat(product['basePrice'] || 0);
                        const value = price * qty;

                        const trackPayload = {
                            value: value,
                            currency: 'IDR',
                            items: [
                                {
                                    item_id: product['menuCode'] || '',
                                    item_name: product['menuName'] || '',
                                    item_category: product['cateCode'] || '',
                                    price: price,
                                    quantity: qty,
                                },
                            ],
                        };

                        trackAddToCart(trackPayload);
                        handleAddToCart(payload, () => {
                            handleClose();
                        });

                        return;
                    }
                }}
                title='Add to Cart'
                desc='Please confirm the date, timeslot, and quantity of your order are correct.'
            />

            <Modal isOpen={isOpenTnc} onClose={onCloseTnc} size='4xl' scrollBehavior='inside'>
                <ModalOverlay />
                <ModalContent rounded='xl'>
                    <ModalBody p={0}>
                        <Box className='tnc-container' p={5}>
                            {new HtmlToReactParser().parse(faqParty)}
                        </Box>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme='primary' px={10} onClick={onCloseTnc}>
                            I Understand
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </NavigationScroll>
    );
}

const MenuPackages = ({ packages = [], productData, selectedAddonsDetail = [], loadingDetail, handleSelectAddonDetail }) => {
    const safePackages = packages || [];

    if (safePackages.length === 0) return null;
    return (
        <Box w='full' my={8}>
            {/* <Heading fontSize={{ base: 'xl', md: 'xl' }} mb={5} fontWeight={500}>
                Packages
            </Heading> */}

            {safePackages.map((pkg) => {
                const packageId = pkg?.id || pkg?.menuPackageCode;
                const details = pkg?.details || [];
                const isMultiSelection = String(pkg?.isMultiSelection) === '1' ?? false;

                // 1. Hitung Batas Maksimal Global (Akumulasi)
                const productQty = productData?.qty || 1;
                const minLimit = isMultiSelection ? 0 : pkg?.minQty || 0;
                const maxLimit = (pkg?.maxQty || 0) * productQty;
                const isRequired = minLimit > 0;

                // 2. Hitung TOTAL QUANTITY yang sudah dipilih dalam paket ini saja
                // agar item S + M + L + XL tidak melebihi maxLimit
                const totalQtyInPkg = details.reduce((sum, detail) => {
                    const found = selectedAddonsDetail.find((s) => s.id === detail.id);
                    return sum + (found?.qty || 0);
                }, 0);

                // 3. Hitung sisa kuota yang tersedia untuk seluruh paket
                const remainingQuota = maxLimit - totalQtyInPkg;

                return (
                    <Box key={packageId} w='full' bg='gray.50' rounded='lg' p={3} border='1px' borderColor='gray.100' mb={4}>
                        <Box mb={5}>
                            <Heading fontSize={{ base: 'md', md: 'lg' }} fontWeight={600}>
                                {pkg?.menuPackageName}
                                {/* {isRequired ? '- Required' : ''} */}
                            </Heading>
                            {/* <Flex justifyContent='space-between'>
                                {isMultiSelection && (
                                    <Text fontSize='xs' color='blue.500'>
                                        Quota Remaining: {remainingQuota}
                                    </Text>
                                )}
                            </Flex> */}
                        </Box>

                        <Box w='full'>
                            <ProductTickets
                                productData={productData}
                                isLoading={loadingDetail}
                                tickets={details}
                                selectedItems={selectedAddonsDetail}
                                isProductRecurring={false}
                                combo={[]}
                                selectedData={{}}
                                misc={{
                                    pkg,
                                    isMultiSelection,
                                    remainingQuota,
                                }}
                                // Jika MultiSelection, jatah per item bisa sampai maxLimit (akumulasi)
                                minQty={minLimit}
                                // maxQty={isMultiSelection ?  : Math.floor(maxLimit / (details.length || 1))}
                                maxQty={maxLimit}
                                onChangeQty={(item, newQty) => {
                                    // Ambil qty item ini yang ada di state sekarang
                                    const currentItem = selectedAddonsDetail.find((s) => s.id === item.id);
                                    const oldQty = currentItem?.qty || 0;

                                    // LOGIKA VALIDASI AKUMULASI:
                                    // (Total Paket Saat Ini) - (Qty Item Ini yang Lama) + (Qty Baru yang Diinput)
                                    const potentialTotal = +(totalQtyInPkg ?? '0') - +(oldQty ?? '0') + +(newQty ?? '0');

                                    if (isMultiSelection) {
                                        if (potentialTotal <= maxLimit) {
                                            // Jika total gabungan masih aman, jalankan update
                                            handleSelectAddonDetail(item, newQty);
                                        } else {
                                            // Jika melebihi batas akumulasi, paksa ke sisa jatah yang ada
                                            // Contoh: sudah pilih 10/12, lalu mau tambah jadi 5 di item ini.
                                            // Maka yg dibolehkan cuma oldQty + sisa (2).
                                            const allowedQty = oldQty + remainingQuota;
                                            handleSelectAddonDetail(item, allowedQty);
                                        }
                                    } else {
                                        // Logika prorated biasa (jika bukan multi-selection)
                                        // const proratedMax = Math.floor(maxLimit / (details.length || 1));
                                        const proratedMax = maxLimit;

                                        if (newQty <= proratedMax) {
                                            handleSelectAddonDetail(item, newQty);
                                        }
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};
